import type { Lot, Portfolio } from 'venn-api';
import { compact, sumBy } from 'lodash';
import type { Theme } from 'venn-ui-kit';
import { ColorUtils } from 'venn-ui-kit';
import { isCashNode, isFundNode, isStrategyNode } from 'venn-utils';
import type { CompareType } from '../contexts/comparison-context';

export enum TreeItemUpdateType {
  'CHANGE_ALLOCATION',
  'ADD_FUND',
  'UPLOAD_FUND',
  'DELETE_PORTFOLIO',
  'DELETE_PORTFOLIO_CHILD',
  'ADD_STRATEGY', // Add empty strategy
  'DRAG_N_DROP',
  'DRAG_N_DROP_REORDER_ONLY',
  'RESET',
  'SAVE',
  'ADD_FROM_GHOST',
  'EDIT_NAME',
  'EXTERNAL_DELETE',
  'APPLY_ALLOCATIONS_FROM_COMPARE',
  'EDIT_CAPITAL',
  'IMPORT_STRATEGY', // Import existing strategy
  'CHANGE_DESIGNATION',
  'CHANGE_LOTS',
}

export const checkHasQuantDiff = (value: number, compareValue: number, divider: number) =>
  Math.round(Math.abs(value - compareValue) * divider) / divider >= 1 / divider;

/**
 * When it's in percentage mode, we will keep previous base allocation
 * UNLESS we're doing cash reallocation (update type EDIT_CAPITAL)
 */
export const getCurrentBaseAllocation = (
  prevBaseAllocation: number,
  currentBaseAllocation: number,
  isPercentageMode: boolean,
  updateType: TreeItemUpdateType,
): number => {
  if (isPercentageMode && updateType !== TreeItemUpdateType.EDIT_CAPITAL) {
    return prevBaseAllocation;
  }
  return currentBaseAllocation;
};

export const getWithReplacedStrategy = (
  portfolio: Portfolio,
  replaceStrategyId: number,
  allCompareNodes: Map<number, Portfolio>,
): Portfolio => {
  const { updatedPortfolio } = getWithMaybeReplacedInnerStrategy(portfolio, replaceStrategyId, allCompareNodes);
  if (updatedPortfolio === null) {
    return {
      ...portfolio,
      allocation: 0,
      cashBalance: 0,
      netAssets: 0,
      children: [],
    };
  }
  return updatedPortfolio;
};

const getWithMaybeReplacedInnerStrategy = (
  portfolio: Portfolio,
  replaceStrategyId: number,
  allCompareNodes: Map<number, Portfolio>,
): { updatedPortfolio: Portfolio | null; hasUpdated: boolean } => {
  if (portfolio.id === replaceStrategyId) {
    return { updatedPortfolio: getReplaced(portfolio, allCompareNodes), hasUpdated: true };
  }

  for (let i = 0; i < portfolio.children.length; i++) {
    const { updatedPortfolio: updatedChild, hasUpdated } = getWithMaybeReplacedInnerStrategy(
      portfolio.children[i]!,
      replaceStrategyId,
      allCompareNodes,
    );

    if (hasUpdated) {
      const children = compact([...portfolio.children.slice(0, i), updatedChild, ...portfolio.children.slice(i + 1)]);
      return {
        updatedPortfolio: {
          ...portfolio,
          children,
          allocation: sumBy(children, 'allocation'),
          cashBalance: sumBy(children, 'cashBalance'),
          netAssets: sumBy(children, 'netAssets'),
        },
        hasUpdated: true,
      };
    }
  }

  return { updatedPortfolio: portfolio, hasUpdated: false };
};

const getKey = (node: Portfolio): string => {
  if (isFundNode(node)) {
    return `fund-${node.fund?.id}`;
  }
  if (isCashNode(node)) {
    return `cash-${node.name}`;
  }
  return `strategy-${node.name}`;
};

const getReplaced = (node: Portfolio, allCompareNodes: Map<number, Portfolio>): Portfolio | null => {
  const compareNode = allCompareNodes.get(node.id);
  if (!compareNode) {
    return null;
  }
  if (isFundNode(node)) {
    return {
      ...node,
      allocation: compareNode.allocation,
      netAssets: compareNode.netAssets,
    };
  }
  if (isCashNode(node)) {
    return {
      ...node,
      id: compareNode.id,
      fund: undefined,
      cashBalance: compareNode.cashBalance,
      netAssets: compareNode.netAssets,
    };
  }
  const ghostChildren = new Map<string, Portfolio[]>();
  compareNode.children.forEach((child) => {
    const key = getKey(child);
    const nodes = ghostChildren.get(key);

    if (!nodes) {
      ghostChildren.set(key, [child]);
    } else {
      ghostChildren.set(key, [...nodes, child]);
    }
  });
  const children: Portfolio[] = [];
  const existingStrategyNames = new Set<string>();
  node.children.forEach((child) => {
    const childCompareNode = allCompareNodes.get(child.id);

    if (childCompareNode) {
      const key = getKey(child);
      const nodes = ghostChildren.get(key);
      if (nodes && nodes.length > 1) {
        nodes.shift();
      } else {
        ghostChildren.delete(key);
      }
    }

    const updatedChild = getReplaced(child, allCompareNodes);
    if (updatedChild !== null) {
      children.push(updatedChild);
      if (isStrategyNode(updatedChild)) {
        existingStrategyNames.add(updatedChild.name);
      }
    }
  });
  [...ghostChildren.values()]
    .flat()
    // deduplicate strategies with the same, they should have already been added in the previous forEach
    .filter((ghostChild) => !isStrategyNode(ghostChild) || !existingStrategyNames.has(ghostChild.name))
    .forEach((ghostChild) => {
      children.push(ghostChild);
    });
  return {
    ...node,
    children,
    allocation: sumBy(children, 'allocation'),
    cashBalance: sumBy(children, 'cashBalance'),
    netAssets: sumBy(children, 'netAssets'),
  };
};

/**
 * Returns the primary & lighter color
 */
export const getColorsFromCompareType = (compareType: CompareType | undefined, theme: Theme): [string, string] => {
  const { Colors } = theme;
  const Blue = Colors.DEPRECATED_DataLineColor.DarkBlue;
  const LightBlue = ColorUtils.hex2rgba(Blue, 0.5);

  const Purple = Colors.HighlightDark;
  const LightPurple = Colors.HighlightLight;

  return compareType === 'Optimized' ? [Purple, LightPurple] : [Blue, LightBlue];
};

export const computeLotsTotalMarketValue = (lots: Lot[]): number =>
  lots.reduce((sum, lot) => sum + (lot.marketValue ?? 0), 0);

const isLotIncomplete = (lot: Lot): boolean => {
  const hasData = !!lot.marketValue || !!lot.costBasis || !!lot.quantity;
  const hasMissing = !lot.marketValue || !lot.costBasis || !lot.quantity;
  return hasData && hasMissing;
};

export const hasNodeIncompleteTaxInfo = (node: Portfolio): boolean => {
  if (!node.lots || node.lots.length === 0) {
    return false;
  }
  return node.lots.some(isLotIncomplete);
};

export const hasPortfolioIncompleteTaxInfo = (portfolio: Portfolio): boolean => {
  if (hasNodeIncompleteTaxInfo(portfolio)) {
    return true;
  }
  return portfolio.children?.some((child) => hasPortfolioIncompleteTaxInfo(child)) ?? false;
};
